import React from "react"
import PropTypes from "prop-types"
import cn from "classnames"

import { SvgNormalizer } from "@asphalt-react/svg-normalizer"
import { svgUtil, propsUtil } from "@asphalt-react/helper"

import { ICON_SIZES } from "./values.js"
import { resolvedSize } from "./validate.js"

import styles from "./content-switcher.module.css"

const Base = ({ children, ...props }) => {
  return (
    <button {...props} role="tab" type="button">
      {children}
    </button>
  )
}

const Icon = ({ children, size, end, ...props }) => {
  if (!children) {
    return null
  }

  if (!svgUtil.isSVG(children)) {
    console.warn(
      "ContentSwitcher: Switch only supports SVG for icons in caption"
    )
    return null
  }

  return (
    <span
      data-testid="icon-wrapper"
      className={cn(styles.qualifier, { [styles.qualifierEnd]: end })}
      {...props}
    >
      <SvgNormalizer dimension={size}>{children}</SvgNormalizer>
    </span>
  )
}

export const Switch = ({
  children,
  value,
  active,
  onAction,
  size: rawSize = "m",
  icon = false,
  qualifier,
  qualifierEnd = false,
  accent = false,
  ...props
}) => {
  // filtering out style and className
  /* eslint-disable-next-line react/prop-types */
  const { style, className, onClick: userOnClick, ...delegated } = props

  const { invalid, size } = resolvedSize(rawSize)

  if (invalid) {
    console.warn(`Switch: Invalid size prop value. Defaulting to ${size}`)
  }

  const switchClass = cn(styles.switch, {
    [styles.selected]: active,
    [styles.selectedAccent]: active && accent,
  })

  const clickHandler = (event) => {
    userOnClick?.(event)
    onAction?.(value, { event })
  }

  const baseProps = {
    ...delegated,
    tabIndex: active ? "-1" : "0",
    "aria-selected": active,
    onClick: clickHandler,
  }

  if (icon) {
    const iconSizeClass = propsUtil.concatClasses(`icon`, size)

    return (
      <Base {...baseProps} className={cn(switchClass, styles[iconSizeClass])}>
        <Icon size={ICON_SIZES[size]}>{children}</Icon>
      </Base>
    )
  }

  return (
    <Base {...baseProps} className={cn(switchClass, styles[size])}>
      <Icon role="none" end={qualifierEnd} size={ICON_SIZES[size]}>
        {qualifier}
      </Icon>
      <span>{children}</span>
    </Base>
  )
}

Switch.displayName = "Switch"

Switch.propTypes = {
  /**
   * Switch caption.
   */
  children: PropTypes.node.isRequired,
  /**
   * Index of the Switch.
   */
  value: PropTypes.number.isRequired,
  /**
   * Adds styles to show the Switch is active.
   */
  active: PropTypes.bool,
  /**
   * Callback to handle Switch selection.
   *
   * The function accepts 2 arguments:
   *
   * - value: `value` prop of the Switch.
   * - options: object containing the React synthetic event.
   *
   * ```js
   * onAction(value, { event }) {
   *  console.log(value)
   * }
   * ```
   */
  onAction: PropTypes.func,
  /**
   * Size of the Switches. Accepts one of "s" or "m" for small & medium.
   */
  size: PropTypes.oneOf(["s", "m"]),
  /**
   * Renders icon as caption.
   */
  icon: PropTypes.bool,
  /**
   * Icons to add more context to the textual caption.
   *
   * Accepts SVG or SVG wrapped React component. Checkout `@asphalt-react/iconpack` for SVG wrapped React components.
   *
   * > ⚠️ Do not use `qualifier` to render a Switch with icon as caption; use `icon` prop instead.
   */
  qualifier: PropTypes.element,
  /**
   * Appends qualifier to the text in caption. Switch prepends the qualifier by default.
   */
  qualifierEnd: PropTypes.bool,
  /**
   * Applies accent intent when Switch is active.
   */
  accent: PropTypes.bool,
}

Switch.defaultProps = {
  size: "m",
  icon: false,
  qualifierEnd: false,
  accent: false,
}
